import * as THREE from 'three';

/**
 * A canonical humanoid bone key (e.g. 'hips', 'leftforearm', 'lefthandindex1').
 * Source (Motive) and target (Mixamo) bone names are reduced to these so we can
 * map between differing naming schemes.
 */
type Canonical = string;

/**
 * Aliases mapping cleaned bone-name tokens onto canonical keys. Covers Mixamo,
 * standard biped (3ds Max), common Motive/OptiTrack naming, and Move.AI (whose
 * names are Mixamo-style once the `_N` actor prefix is cleaned). Extend freely.
 * Finger aliases are appended programmatically below.
 */
const ALIASES: Record<string, Canonical> = {
  hips: 'hips',
  hip: 'hips',
  pelvis: 'hips',
  spine: 'spine',
  spine1: 'spine1',
  spine2: 'spine2',
  ab: 'spine',
  chest: 'chest',
  spine3: 'chest',
  neck: 'neck',
  neck1: 'neck',
  head: 'head',

  leftshoulder: 'leftshoulder',
  lshoulder: 'leftshoulder',
  lcollar: 'leftshoulder',
  leftarm: 'leftarm',
  luarm: 'leftarm',
  lupperarm: 'leftarm',
  leftupperarm: 'leftarm',
  leftforearm: 'leftforearm',
  lfarm: 'leftforearm',
  lforearm: 'leftforearm',
  leftlowerarm: 'leftforearm',
  lefthand: 'lefthand',
  lhand: 'lefthand',

  rightshoulder: 'rightshoulder',
  rshoulder: 'rightshoulder',
  rcollar: 'rightshoulder',
  rightarm: 'rightarm',
  ruarm: 'rightarm',
  rupperarm: 'rightarm',
  rightupperarm: 'rightarm',
  rightforearm: 'rightforearm',
  rfarm: 'rightforearm',
  rforearm: 'rightforearm',
  rightlowerarm: 'rightforearm',
  righthand: 'righthand',
  rhand: 'righthand',

  leftupleg: 'leftupleg',
  lthigh: 'leftupleg',
  leftthigh: 'leftupleg',
  leftupperleg: 'leftupleg',
  leftleg: 'leftleg',
  lshin: 'leftleg',
  leftshin: 'leftleg',
  leftlowerleg: 'leftleg',
  leftfoot: 'leftfoot',
  lfoot: 'leftfoot',
  lefttoe: 'lefttoe',
  lefttoebase: 'lefttoe',
  ltoe: 'lefttoe',

  rightupleg: 'rightupleg',
  rthigh: 'rightupleg',
  rightthigh: 'rightupleg',
  rightupperleg: 'rightupleg',
  rightleg: 'rightleg',
  rshin: 'rightleg',
  rightshin: 'rightleg',
  rightlowerleg: 'rightleg',
  rightfoot: 'rightfoot',
  rfoot: 'rightfoot',
  righttoe: 'righttoe',
  righttoebase: 'righttoe',
  rtoe: 'righttoe',
};

// Fingers: Motive ("RightHandIndex1") and Mixamo ("mixamorigRightHandIndex1")
// both clean to "righthandindex1", so the canonical key is the cleaned name and
// the alias is an identity mapping. 5 fingers × joints 1–3 × 2 sides.
const FINGERS = ['thumb', 'index', 'middle', 'ring', 'pinky'] as const;
for (const side of ['left', 'right'] as const) {
  for (const finger of FINGERS) {
    for (let joint = 1; joint <= 3; joint++) {
      const key = `${side}hand${finger}${joint}`; // e.g. lefthandindex1
      ALIASES[key] = key;
      // Some rigs use a short side prefix without "hand" (e.g. "LIndex1").
      ALIASES[`${side[0]}${finger}${joint}`] = key;
    }
  }
}

/** Strip namespace prefixes (mixamorig:, Skeleton:, Bip01_, _1) and punctuation. */
function clean(name: string): string {
  let n = name.toLowerCase();
  n = n.replace(/^.*[:|]/, ''); // drop "mixamorig:" / "actor|" style prefixes
  // FBXLoader strips the ':' from "mixamorig:Hips" → "mixamorigHips"; remove the
  // bare prefix too.
  n = n.replace(/^mixamorig\d*/, '');
  n = n.replace(/^bip\d*_?/, ''); // 3ds Max biped prefix
  n = n.replace(/^_\d+/, ''); // Move.AI per-actor prefix ("_1Hips", "_2LeftArm")
  n = n.replace(/[\s_.-]/g, ''); // remove separators
  return n;
}

/** Canonical humanoid key for a bone name ('hips', 'head', …), or null. */
export function toCanonical(name: string): Canonical | null {
  const c = clean(name);
  return ALIASES[c] ?? null;
}

interface BonePair {
  source: THREE.Object3D;
  target: THREE.Object3D;
  /** The humanoid slot this pair fills ('hips', 'leftarm', …). */
  canonical: Canonical;
  /** Constant correction: sourceBindWorld⁻¹ · targetBindWorld. */
  correction: THREE.Quaternion;
  /** Number of ancestors of the target bone — used to apply parents first. */
  depth: number;
}

/**
 * Per-actor spatial adjustment ("Adjust Actor Offset"): a world-space offset
 * added to the puppet's root placement plus a uniform size multiplier. The
 * retargeter holds a live reference and reads it every apply(), so UI tweaks
 * show immediately and exports bake them for free. Mutate in place — never
 * replace the object, or the retargeter keeps reading the stale one.
 */
export interface ActorAdjust {
  offset: [number, number, number];
  scale: number;
}

/** A keyframed adjustment: at `time` (seconds) the actor holds these values. */
export interface AdjustKey {
  time: number;
  offset: [number, number, number];
  scale: number;
}

/**
 * Evaluate adjustment keys at time t INTO the live ActorAdjust (mutating it in
 * place — the retargeter holds the reference). Smoothstep between neighbouring
 * keys, clamped to the first/last outside the keyed range — the same feel as
 * contact-pin influence, so an avatar can "land" at a spot by a given time.
 */
export function evalAdjustKeys(keys: AdjustKey[], t: number, out: ActorAdjust): void {
  if (keys.length === 0) return;
  let k1 = keys[0];
  let k2 = keys[0];
  for (let i = 0; i < keys.length; i++) {
    if (keys[i].time <= t) {
      k1 = keys[i];
      k2 = keys[Math.min(i + 1, keys.length - 1)];
    } else {
      if (i === 0) k2 = keys[0];
      break;
    }
  }
  const span = k2.time - k1.time;
  const s = span > 1e-9 ? Math.min(Math.max((t - k1.time) / span, 0), 1) : 1;
  const eased = s * s * (3 - 2 * s);
  out.offset[0] = k1.offset[0] + (k2.offset[0] - k1.offset[0]) * eased;
  out.offset[1] = k1.offset[1] + (k2.offset[1] - k1.offset[1]) * eased;
  out.offset[2] = k1.offset[2] + (k2.offset[2] - k1.offset[2]) * eased;
  out.scale = k1.scale + (k2.scale - k1.scale) * eased;
}

/** Source bind pose captured before the mixer first runs: per-bone world rotation + world position. */
export interface SourceBindPose {
  world: Map<THREE.Object3D, THREE.Quaternion>;
  worldPos: Map<THREE.Object3D, THREE.Vector3>;
  /** Bind-pose LOCAL rotations (the finger healer measures deviation from these). */
  local: Map<THREE.Object3D, THREE.Quaternion>;
}

export interface Retargeter {
  pairs: BonePair[];
  matched: Canonical[];
  unmatched: string[];
  /**
   * Hips placement from the last apply(): `raw` = where the mocap puts the
   * puppet before the user's ActorAdjust, `adjusted` = the final position.
   * Feeds the offset gizmo + deviation line viz.
   */
  hipsInfo: { raw: THREE.Vector3; adjusted: THREE.Vector3 };
  /** Copy current source pose onto the puppet. Call after mixer.update. */
  apply: () => void;
  /**
   * Experimental: pin every matched target joint's world position onto the
   * source joint each apply(), so joints (hands!) land exactly where the
   * actor's did. The avatar's mesh warps to follow. Off restores the rig's
   * original bone offsets. NOTE: while on, every driven bone's position
   * animates — exports must bake position tracks for all of them.
   */
  setProportionMatch: (on: boolean) => void;
}

/** Capture a skeleton's bind pose (world rotations) before any animation runs. */
export function captureBindPose(
  root: THREE.Object3D,
  bones: THREE.Object3D[]
): SourceBindPose {
  root.updateWorldMatrix(true, true);
  const world = new Map<THREE.Object3D, THREE.Quaternion>();
  const worldPos = new Map<THREE.Object3D, THREE.Vector3>();
  const local = new Map<THREE.Object3D, THREE.Quaternion>();
  for (const bone of bones) {
    world.set(bone, bone.getWorldQuaternion(new THREE.Quaternion()));
    worldPos.set(bone, bone.getWorldPosition(new THREE.Vector3()));
    local.set(bone, bone.quaternion.clone());
  }
  return { world, worldPos, local };
}

function ancestorCount(obj: THREE.Object3D): number {
  let n = 0;
  let p = obj.parent;
  while (p) {
    n++;
    p = p.parent;
  }
  return n;
}

/**
 * Build a retargeter between a source (animated Motive skeleton) and a target
 * puppet mesh (SPEC §3.2). Transfers each bone's orientation in **world space**:
 * `targetWorld = sourceWorld · (sourceBindWorld⁻¹ · targetBindWorld)`, then converts
 * back to the target's local frame. Working in world space (rather than copying
 * local quaternions) makes it robust to the differing bone-axis conventions and
 * rest poses between Motive and Mixamo rigs.
 *
 * `sourceBind` must be the source's true bind pose, captured *before* the mixer
 * first poses it (see captureBindPose / installMocap).
 */
export function createRetargeter(
  sourceBones: THREE.Object3D[],
  targetBones: THREE.Object3D[] | Map<Canonical, THREE.Object3D>,
  sourceBind: SourceBindPose,
  /**
   * User remaps from the bone-mapping UI: canonical slot → source bone NAME.
   * An entry claims its slot exclusively (auto-matching is bypassed for it);
   * an empty-string name leaves the slot deliberately unassigned.
   */
  sourceOverrides?: Map<Canonical, string>,
  adjust?: ActorAdjust
): Retargeter {
  const overrideByName = new Map<string, Canonical>();
  const overriddenSlots = new Set<Canonical>();
  if (sourceOverrides) {
    for (const [canonical, boneName] of sourceOverrides) {
      overriddenSlots.add(canonical);
      if (boneName) overrideByName.set(boneName, canonical);
    }
  }
  // Targets are matched by canonical key: either derived from bone names (FBX /
  // GLB meshes) or supplied explicitly (VRM humanoid map, custom bone mapping).
  let targetByKey: Map<Canonical, THREE.Object3D>;
  if (targetBones instanceof Map) {
    targetByKey = targetBones;
  } else {
    targetByKey = new Map<Canonical, THREE.Object3D>();
    for (const b of targetBones) {
      const key = toCanonical(b.name);
      if (key && !targetByKey.has(key)) targetByKey.set(key, b);
    }
  }

  const pairs: BonePair[] = [];
  const matched: Canonical[] = [];
  const unmatched: string[] = [];
  // An explicit map may alias several canonicals to one node (e.g. chest &
  // spine2 on a rig without upperChest) — pair each target bone only once.
  const usedTargets = new Set<THREE.Object3D>();

  for (const source of sourceBones) {
    let key = overrideByName.get(source.name) ?? toCanonical(source.name);
    // A slot claimed by an override is off-limits to auto-matched bones.
    if (key && overriddenSlots.has(key) && overrideByName.get(source.name) !== key) key = null;
    let target = key ? targetByKey.get(key) : undefined;
    if (target && usedTargets.has(target)) target = undefined;
    const srcBindWorld = sourceBind.world.get(source);
    if (key && target && srcBindWorld) {
      usedTargets.add(target);
      const tgtBindWorld = target.getWorldQuaternion(new THREE.Quaternion());
      // correction = sourceBindWorld⁻¹ · targetBindWorld
      const correction = srcBindWorld.clone().invert().multiply(tgtBindWorld);
      pairs.push({ source, target, canonical: key, correction, depth: ancestorCount(target) });
      matched.push(key);
    } else {
      unmatched.push(source.name);
    }
  }

  // Apply parents before children so each child reads its parent's updated world.
  pairs.sort((a, b) => a.depth - b.depth);

  // Hips placement: drive the puppet hips to the source hips' ABSOLUTE world
  // position (height-scaled). Absolute (not delta-from-rest) placement keeps each
  // puppet anchored at its own actor's spot when several skeletons share a take.
  const hipsPair = pairs.find((p) => p.canonical === 'hips');
  const sourceHipsBindWorld = hipsPair ? sourceBind.worldPos.get(hipsPair.source) : undefined;
  let targetHipsBindWorldY = 1;
  if (hipsPair) {
    hipsPair.target.updateWorldMatrix(true, false);
    targetHipsBindWorldY = hipsPair.target.getWorldPosition(new THREE.Vector3()).y;
  }
  const heightRatio =
    sourceHipsBindWorld && sourceHipsBindWorld.y !== 0
      ? targetHipsBindWorldY / sourceHipsBindWorld.y
      : 1;

  const srcWorld = new THREE.Quaternion();
  const parentWorld = new THREE.Quaternion();
  const desired = new THREE.Quaternion();
  const hipsWorldPos = new THREE.Vector3();
  const hipsInfo = { raw: new THREE.Vector3(), adjusted: new THREE.Vector3() };

  // --- Proportion matching (experimental) --------------------------------------
  // Original local positions of every driven bone, for restore on toggle-off.
  // Rescaling target bone lengths can't fully align joints (bind-pose segment
  // DIRECTIONS also differ between rigs), so this instead pins every matched
  // joint's world position onto the source joint each apply() — exact by
  // construction; the mesh stretches to follow.
  const originalPositions = new Map<THREE.Object3D, THREE.Vector3>();
  let proportionMatch = false;

  function setProportionMatch(on: boolean): void {
    if (on === proportionMatch) return;
    proportionMatch = on;
    if (on) {
      for (const pair of pairs) {
        originalPositions.set(pair.target, pair.target.position.clone());
      }
    } else {
      for (const [node, pos] of originalPositions) node.position.copy(pos);
      originalPositions.clear();
    }
  }

  const pinWorld = new THREE.Vector3();

  function apply(): void {
    // Root placement FIRST (it doesn't depend on the rotations below): puppet
    // hips → source hips world position (height-scaled), then the user's actor
    // adjustment (size multiplier + world offset). Placing it before the loop
    // lets the proportion-match pins convert against the final hips position.
    if (hipsPair) {
      hipsPair.source.getWorldPosition(hipsWorldPos);
      // Proportion match wants joints exactly on the mocap's, so no height scale.
      hipsWorldPos.y *= proportionMatch ? 1 : heightRatio;
      hipsInfo.raw.copy(hipsWorldPos);
      if (adjust) {
        hipsWorldPos.y *= adjust.scale; // a resized avatar keeps its feet planted
        hipsWorldPos.x += adjust.offset[0];
        hipsWorldPos.y += adjust.offset[1];
        hipsWorldPos.z += adjust.offset[2];
      }
      hipsInfo.adjusted.copy(hipsWorldPos);
      const parent = hipsPair.target.parent;
      if (parent) {
        parent.updateWorldMatrix(true, false);
        parent.worldToLocal(hipsWorldPos);
      }
      hipsPair.target.position.copy(hipsWorldPos);
    }

    for (const pair of pairs) {
      const { source, target, correction } = pair;
      source.getWorldQuaternion(srcWorld); // self-updates from the mixer-posed bone
      desired.copy(srcWorld).multiply(correction); // targetWorld = sourceWorld · correction
      if (target.parent) {
        target.parent.getWorldQuaternion(parentWorld); // reflects already-set ancestors
        target.quaternion.copy(parentWorld.invert()).multiply(desired);
      } else {
        target.quaternion.copy(desired);
      }
      // Proportion match: pin the joint's world position onto the source joint
      // (with the same user adjustment the hips get). Parents are processed
      // first, so each conversion sees its ancestors' pinned positions.
      if (proportionMatch && pair !== hipsPair) {
        source.getWorldPosition(pinWorld);
        if (adjust) {
          pinWorld.y *= adjust.scale;
          pinWorld.x += adjust.offset[0];
          pinWorld.y += adjust.offset[1];
          pinWorld.z += adjust.offset[2];
        }
        if (target.parent) {
          target.parent.updateWorldMatrix(true, false);
          target.parent.worldToLocal(pinWorld);
        }
        target.position.copy(pinWorld);
      }
    }
  }

  return { pairs, matched, unmatched, hipsInfo, apply, setProportionMatch };
}
